/**
 * Trailing-candle metrics
 *
 * The chart already draws a "trailing" mini-bar offset inside each candle: the
 * OHLC of only the final N seconds of that candle (30s by default). This turns
 * that bar into the numbers the WCC info overlay reports — body / wick / total
 * lengths, and how much of the PARENT candle each one accounts for.
 *
 * The parent denominator is the parent's high-low range, matching what WCC
 * itself calls `candleLength` (strategy/mod.rs: `candle.high - candle.low`) and
 * measures its wick percentage against. Using anything else here would make two
 * percentages on the same strip mean different things.
 *
 * Pure functions — no React, no stores.
 */

import type { TrailingOHLC } from '@shared/types';

/** Minimum OHLC shape needed from the parent candle. */
export interface ParentCandleOHLC {
  open: number;
  high: number;
  low: number;
  close: number;
}

export interface TrailingCandleMetrics {
  /** Window the trailing bar covers, in seconds (30 by default). */
  durationSeconds: number;

  // --- Absolute lengths, in points ---
  /** Trailing high - low. */
  totalPts: number;
  /** |trailing close - trailing open|. */
  bodyPts: number;
  upperWickPts: number;
  lowerWickPts: number;
  /** Both wicks combined. */
  combinedWickPts: number;

  // --- Parent context ---
  /** Parent high - low, the denominator for every percentage below. */
  parentLengthPts: number;
  /** Trailing range as a % of the parent candle's range. */
  totalPctOfParent: number;
  bodyPctOfParent: number;
  upperWickPctOfParent: number;
  lowerWickPctOfParent: number;
  combinedWickPctOfParent: number;

  /** Body as a % of the TRAILING bar's own range (how clean the last N sec were). */
  bodyPctOfTrailing: number;

  /**
   * Trailing close >= open.
   *
   * Uses `>=` to match TrailingCandlePrimitive, which colours a flat bar with
   * the bullish fill. A stricter `>` here would tint the overlay row bearish
   * while the bar on the chart is cyan.
   */
  isBullish: boolean;
}

/**
 * Build the metrics, or null when they'd be meaningless.
 *
 * Returns null if the trailing data is absent/invalid or the parent candle has
 * no range — better to render a dash than a divide-by-zero percentage.
 */
export function computeTrailingCandleMetrics(
  parent: ParentCandleOHLC | null | undefined,
  trailing: TrailingOHLC | null | undefined
): TrailingCandleMetrics | null {
  if (!parent || !trailing || !trailing.isValid) return null;

  const parentLengthPts = parent.high - parent.low;
  if (!Number.isFinite(parentLengthPts) || parentLengthPts <= 0) return null;

  const totalPts = trailing.high - trailing.low;
  if (!Number.isFinite(totalPts) || totalPts < 0) return null;

  const bodyTop = Math.max(trailing.open, trailing.close);
  const bodyBottom = Math.min(trailing.open, trailing.close);
  const bodyPts = bodyTop - bodyBottom;
  const upperWickPts = trailing.high - bodyTop;
  const lowerWickPts = bodyBottom - trailing.low;
  const combinedWickPts = upperWickPts + lowerWickPts;

  const pctOfParent = (v: number): number => (v / parentLengthPts) * 100;

  return {
    durationSeconds: trailing.durationSeconds,

    totalPts,
    bodyPts,
    upperWickPts,
    lowerWickPts,
    combinedWickPts,

    parentLengthPts,
    totalPctOfParent: pctOfParent(totalPts),
    bodyPctOfParent: pctOfParent(bodyPts),
    upperWickPctOfParent: pctOfParent(upperWickPts),
    lowerWickPctOfParent: pctOfParent(lowerWickPts),
    combinedWickPctOfParent: pctOfParent(combinedWickPts),

    // A flat trailing bar has no range; report 0 rather than NaN.
    bodyPctOfTrailing: totalPts > 0 ? (bodyPts / totalPts) * 100 : 0,

    isBullish: trailing.close >= trailing.open,
  };
}
